/* @flow */
import { css } from 'linaria';
import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import Colors from '../../constants/Colors';
import { RequestInAppBlock, ReviewBlock, ThreeFeaturesBlock } from '../components';
import {
  BodyText,
  Card,
  Container,
  Grid,
  GridCell,
  Icon,
  InlineFeature,
  SectionTitle,
  Spacer,
  Title,
  Well,
} from '../../components/core';
import { PageBody } from '../../components/chrome';
import Responsive from '../../constants/Responsive';
import { ResponsiveEstimateForm } from '../../components/EstimateForm';
import Reviews, { getReviewTagged } from '../../../data/Reviews';
import type { ServicesPageChromeMetadata } from './ServicesPageChrome';
import tripRequests from '../../../redux/trip-requests';
import type { UseCase } from '../../../data/TripRequests';

function Notice({ title, icon, description }: { title: string, icon: any, description: string }) {
  return (
    <Card className={Styles.NoticeCard}>
      <div className={Styles.NoticeIcon}>{icon}</div>
      <Title size="20" weight="bold">
        {title}
      </Title>
      <BodyText size="14">{description}</BodyText>
    </Card>
  );
}

class JunkRemovalPage extends React.Component<{
  setNewTripRequestUseCase: (useCase: UseCase) => void,
}> {
  static metadata = {
    title: 'Junk Removal',
    description:
      'We send a truck and movers same day to remove and haul away your junk to the nearest transfer station. Get an estimate today!',
  };

  static servicesMetadata: ServicesPageChromeMetadata = {
    illustration: 'luggers-moving-junk',
    id: 'junk-removal',
  };

  componentDidMount() {
    const { setNewTripRequestUseCase } = this.props;
    setNewTripRequestUseCase('junk');
  }

  render() {
    return (
      <PageBody>
        <Container padded={true} align="center">
          <SectionTitle title="Junk removal with a tap" />
          <BodyText
            size="16"
            align="center"
            style={{ maxWidth: '630px', marginLeft: 'auto', marginRight: 'auto' }}>
            We send a truck and movers same day to remove and haul away your junk to the nearest
            transfer station.
          </BodyText>
          <Spacer size={80} />
          <ThreeFeaturesBlock
            features={[
              {
                title: 'Snap a picture',
                description:
                  'Take a quick picture of what you want to get removed. It’s that easy.',
                icon: 'take-picture',
              },
              {
                title: 'On your schedule',
                description: 'Two Luggers and a truck will pick up your junk when you’re ready.',
                icon: 'pick-a-time',
              },
              {
                title: 'A clean house',
                description: 'Your junk removed in no time.',
                icon: 'stay-at-home',
              },
            ]}
          />
        </Container>
        <Spacer size={120} />
        <Container size="large" padded={true}>
          <ResponsiveEstimateForm />
        </Container>
        <Spacer size={120} />
        <Container align="center">
          <SectionTitle
            title="Recycle your items"
            subtitle="We're happy to clear out the following items for you"
          />
          <Spacer size={60} />
          <Grid equalHeight={true} style={{ marginTop: 64 }}>
            <GridCell size={Responsive('size12of12', 'size4of12')}>
              <InlineFeature
                className={Styles.InlineFeature}
                icon={<Icon name="bedding" />}
                title="Bedding"
                description="Mattresses (incl. waterbed mattresses), boxsprings, sleeping pillows, and covers."
              />
            </GridCell>
            <GridCell size={Responsive('size12of12', 'size4of12')}>
              <InlineFeature
                className={Styles.InlineFeature}
                icon={<Icon name="broken-tv" />}
                title="Damaged items"
                description="Damaged electronics, dvds, furniture, tools and other items."
              />
            </GridCell>
            <GridCell size={Responsive('size12of12', 'size4of12')}>
              <InlineFeature
                className={Styles.InlineFeature}
                icon={<Icon name="appliances" />}
                title="Small Appliances"
                description="Small refrigerators, freezers, washers/dryers, and other small appliances."
              />
            </GridCell>
          </Grid>
          <Grid style={{ marginTop: 64 }}>
            <GridCell size={Responsive('size12of12', 'size6of12')}>
              <Notice
                title="Closing times"
                description="After you set a dump or recycle station as the destination in the app please take their closing times in account when you select a pick up time. They mostly close early afternoon."
                icon={<Icon name="closing-times" />}
              />
            </GridCell>
            <GridCell size={Responsive('size12of12', 'size6of12')}>
              <Notice
                title="Additional fee"
                description="Dump and recycle stations charge a fee to dispose of your items which will be added to your total fare. This could range from $40 to $100+ based on the size and weight."
                icon={<Icon name="additional-fee" />}
              />
            </GridCell>
          </Grid>
        </Container>
        <Spacer size={120} />
        <Well backgroundColor={Colors.GrayLight} className={Styles.ThingsWeDontTake}>
          <Icon name="warning" className={Styles.ThingsWeDontTakeWarningIcon} />
          <Title weight="bold" size="32" align="center">
            Few things we won&apos;t take
          </Title>
          <BodyText size="14">Check with your recycle center for more information.</BodyText>
          <Grid>
            <GridCell
              size={Responsive('size12of12', 'size6of12')}
              className={Styles.ThingsWeDontTakeUnit}>
              <Icon name="chemicals" className={Styles.ThingsWeDontTakeUnitIcon} />
              <Title size="20" weight="bold">
                Chemicals and liquids
              </Title>
              <BodyText size="14" className={Styles.ThingsWeDontTakeBodyText}>
                We won&apos;t take paint, chemical liquids, chemical waste, or any other type of
                illegal substances.
              </BodyText>
            </GridCell>
            <GridCell
              size={Responsive('size12of12', 'size6of12')}
              className={Styles.ThingsWeDontTakeUnit}>
              <Icon name="raw-materials" className={Styles.ThingsWeDontTakeUnitIcon} />
              <Title size="20" weight="bold">
                Raw materials
              </Title>
              <BodyText size="14" className={Styles.ThingsWeDontTakeBodyText}>
                We won&apos;t take loose trash or raw materials like soil, compost, and misc. garden
                waste such as branches or trees.
              </BodyText>
            </GridCell>
          </Grid>
        </Well>
        <Container size="large" padded={true}>
          <Spacer size={200} color={Colors.Divider} />
        </Container>
        <Container padded={true}>
          <RequestInAppBlock />
        </Container>
        <Container size="large" padded={true}>
          <Spacer size={200} color={Colors.Divider} />
        </Container>
        <Container padded={true}>
          <ReviewBlock review={getReviewTagged(Reviews, 'junk-removal-page')} />
        </Container>
      </PageBody>
    );
  }
}

const Styles = {
  InlineFeature: css`
    margin-bottom: 56px;
  `,
  DonationLocationGrid: css`
    margin-left: -12px;
    margin-right: -12px;
  `,
  DonationLocationGridCell: css`
    padding: 12px;
  `,

  NoticeCard: css`
    background-color: ${Colors.opacity('#F8F8F9', 0.4)};
    margin-left: 12px;
    margin-right: 12px;
    margin-top: 40px;
    padding-top: 0;
    text-align: center;
  `,
  NoticeIcon: css`
    margin-bottom: 24px;
    margin-top: -16px;
  `,
  ThingsWeDontTake: css`
    text-align: center;
  `,
  ThingsWeDontTakeWarningIcon: css`
    margin-bottom: 24px;
  `,
  ThingsWeDontTakeUnit: css`
    margin-top: 60px;
  `,
  ThingsWeDontTakeUnitIcon: css`
    margin-bottom: 24px;
  `,
  ThingsWeDontTakeBodyText: css`
    margin-left: auto;
    margin-right: auto;
    max-width: 380px;
  `,
};

export default compose(
  connect(
    null,
    dispatch => ({
      setNewTripRequestUseCase: useCase =>
        dispatch(tripRequests.actions.setNewTripRequestUseCase(useCase)),
    })
  )
)(JunkRemovalPage);



// WEBPACK FOOTER //
// ./src/ui/pages/ServicesPage/JunkRemovalPage.js